import { MarketingLoadErrorBoundary } from "../marketing-site/MarketingLoadErrorBoundary";
import type { LandingLanguage } from "./landingCopy";
import React from "react";
import { Box } from "@chakra-ui/react";
import { useLandingTheme } from "./LandingThemeContext";
import LandingNav from "./sections/LandingNav";
import HeroSection from "./sections/HeroSection";
import LandingFooter from "./sections/LandingFooter";
import { ThemedQualitySection } from "./sections/themed/ThemedQualitySection";
import { ChartQualitySection } from "./sections/themed/ChartQualitySection";
import { ThemedTestimonials } from "./sections/themed/ThemedTestimonials";
import { ThemedLogoStrip } from "./sections/themed/ThemedLogoStrip";
import { ThemedQuestionTypes } from "./sections/themed/ThemedQuestionTypes";
import { ThemedProgress } from "./sections/themed/ThemedProgress";
import { FieldGuidePlates } from "./sections/themed/FieldGuidePlates";
import { ThemedClosingCta } from "./sections/themed/ThemedClosingCta";
import TestimonialsSection from "./sections/TestimonialsSection";
import UniversityMarquee from "./sections/UniversityMarquee";
import ExamStyleSection from "./sections/ExamStyleSection";
import GrowTreesSection from "./sections/GrowTreesSection";
import LearnByTalkingSection from "./sections/LearnByTalkingSection";

// The page structure shared by every designed version (everything except the Framer replica).
export function ThemedLandingPage({ language = "en" }: { language?: LandingLanguage }) {
  const theme = useLandingTheme();
  // A light page whose hero is a dark surface carries the nav on that surface too.
  const navAndHeroBackground = theme.navOnDark && !theme.isDark ? theme.color.accentDark : undefined;
  return (
    <>
      <Box bg={navAndHeroBackground}>
        <LandingNav language={language} showLanguageSelector={false} />
        <HeroSection />
      </Box>
      <MarketingLoadErrorBoundary key={theme.id} section="landing-content" fallback={null}>
        {theme.usesOriginalSectionLayouts ? (
          // The Moss family keeps the original page's section layouts and copy, restyled in its own tokens.
          <>
            <ChartQualitySection />
            <TestimonialsSection />
            <UniversityMarquee greyed />
            <LearnByTalkingSection />
            <ExamStyleSection />
            <GrowTreesSection />
          </>
        ) : (
          <>
            <ThemedQualitySection />
            <ThemedTestimonials />
            <ThemedLogoStrip />
            <ThemedQuestionTypes />
            {theme.id === "field-guide" ? <FieldGuidePlates /> : <ThemedProgress />}
          </>
        )}
      </MarketingLoadErrorBoundary>
      <ThemedClosingCta />
      <LandingFooter variant="themed" language={language} showLanguageSelector />
    </>
  );
}
